import React, { useState } from 'react';
import styles from './Cell.module.css';

const Cell = ({ cell, grid, size, onCellClick, isSelected }) => {
  // Add state to track hover
  const [isHovered, setIsHovered] = useState(false);
  
  const getCellStyle = () => {
    // Create the base style with grid lines - now thinner and gray
    const style = {
      flex: '1 1 0', // grow, shrink, and base size of 0 to distribute evenly
      aspectRatio: '1', // maintain square shape
      backgroundColor: cell.background_color,
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: `calc(min(60vh, 80vw) / ${size * 2})`,
      cursor: cell.move_allowed ? 'pointer' : 'default',
      opacity: cell.is_visible ? 1 : 0,
      pointerEvents: cell.is_enabled ? 'auto' : 'none',
      border: '0.5px solid #aaaaaa',
    };
    
    // Border rendering is now handled by individual border layer components
    
    // Apply hover style only if the cell is selectable and not already selected
    if (!isSelected && isHovered && cell.move_allowed) {
      style.backgroundColor = cell.background_color
        ? `${cell.background_color}AA` // Even more transparency for hover (uppercase AA for consistency)
        : '#f8f8f8';
    }
    
    return style;
  };

  const getHeaderStyle = () => {
    // Calculate top and left position based on whether outer borders exist
    const topOffset = cell.outer_border_top ? '4px' : '2px'; // Reduced from 6px/3px
    const leftOffset = cell.outer_border_left ? '4px' : '2px'; // Reduced from 6px/3px

    // Scale header font size - larger than before for better readability
    let scaleFactor;
    if (size <= 4) {
      scaleFactor = 4;  // Larger header for 3x3, 4x4 grids
    } else if (size <= 6) {
      scaleFactor = 4.5;  // Larger header for 5x5, 6x6 grids
    } else if (size <= 8) {
      scaleFactor = 5;  // Larger header for 7x7, 8x8 grids
    } else {
      scaleFactor = 5.5;  // Larger header for 9x9 and larger grids
    }

    // Apply background color that syncs with cell hover state
    // Start with the same base color as the cell
    let bgColor = cell.background_color;

    // Apply hover effect - same as cell background
    if (!isSelected && isHovered && cell.move_allowed) {
      bgColor = cell.background_color
        ? `${cell.background_color}AA` // Same transparency as cell hover (uppercase AA for consistency)
        : '#f8f8f8';
    }

    return {
      position: 'absolute',
      top: topOffset,
      left: leftOffset,
      fontSize: `calc(min(60vh, 80vw) / ${size * scaleFactor})`, // Better scaling for readability
      fontWeight: 'bold',
      zIndex: 2, // Lower than cell value (5) but above borders
      backgroundColor: bgColor, // Restored background to block inner borders
      padding: '1px 2px', // Restored padding for better appearance
      borderRadius: '2px',
      color: '#000000', // Ensure text is always black for readability
    };
  };

  const getFooterStyle = () => {
    // Calculate a better scaling factor for notes based on grid size
    // For smaller grids, we want larger notes; for larger grids, smaller notes
    let scaleFactor;
    if (size <= 4) {
      scaleFactor = 3.5;  // Larger font for 3x3, 4x4 grids
    } else if (size <= 6) {
      scaleFactor = 4;  // Medium font for 5x5, 6x6 grids
    } else if (size <= 8) {
      scaleFactor = 4.5;  // Smaller font for 7x7, 8x8 grids
    } else {
      scaleFactor = 5;  // Better readability for 9x9 and larger grids
    }

    // Calculate bottom offset based on whether outer borders exist
    const bottomOffset = cell.outer_border_bottom ? '6px' : '2px';
    const leftOffset = cell.outer_border_left ? '6px' : '0px';
    const rightOffset = cell.outer_border_right ? '6px' : '0px';

    return {
      position: 'absolute',
      bottom: bottomOffset,
      left: leftOffset,
      right: rightOffset,
      // Dynamic font size that scales better with grid size
      fontSize: `calc(min(60vh, 80vw) / ${size * scaleFactor})`,
      textAlign: 'center',
      display: 'flex',
      justifyContent: 'center',
      width: `calc(100% - ${leftOffset} - ${rightOffset})`,
      lineHeight: '1.2',  // Slightly tighter line height for notes
      fontWeight: '600',  // Slightly bolder for better readability
      zIndex: 2, // Same as header, below cell value
    };
  };
  
  // Function to display the footer text with spaces
  const renderFooterText = () => {
    if (!cell.footer_text || cell.footer_text.length === 0) {
      return null;
    }
    
    // Join the footer text values with spaces
    return cell.footer_text.join(' ');
  };

  // Handle cell click with scroll prevention
  const handleCellClick = (x, y) => {
    if (cell.move_allowed) {
      // Prevent scrolling when clicking a cell
      const currentScrollPosition = window.scrollY;
      onCellClick(x, y);
      // Restore scroll position after a short delay to ensure it works
      setTimeout(() => {
        window.scrollTo(0, currentScrollPosition);
      }, 0);
    }
  };

  // Render outer borders (subgrids/cages) - using grid-level style with cell boolean flags
  const renderOuterBorders = () => {
    const borders = [];

    if (cell.outer_border_top && grid?.outer_border_style) {
      borders.push(
        <div
          key="outer-border-top"
          className={styles.outerBorderTop}
          style={{
            borderTopStyle: grid.outer_border_style,
            borderTopColor: '#000',
            borderTopWidth: '4px',
          }}
        />
      );
    }

    if (cell.outer_border_right && grid?.outer_border_style) {
      borders.push(
        <div
          key="outer-border-right"
          className={styles.outerBorderRight}
          style={{
            borderRightStyle: grid.outer_border_style,
            borderRightColor: '#000',
            borderRightWidth: '4px',
          }}
        />
      );
    }

    if (cell.outer_border_bottom && grid?.outer_border_style) {
      borders.push(
        <div
          key="outer-border-bottom"
          className={styles.outerBorderBottom}
          style={{
            borderBottomStyle: grid.outer_border_style,
            borderBottomColor: '#000',
            borderBottomWidth: '4px',
          }}
        />
      );
    }

    if (cell.outer_border_left && grid?.outer_border_style) {
      borders.push(
        <div
          key="outer-border-left"
          className={styles.outerBorderLeft}
          style={{
            borderLeftStyle: grid.outer_border_style,
            borderLeftColor: '#000',
            borderLeftWidth: '4px',
          }}
        />
      );
    }

    return borders;
  };

  // Render inner borders (killer sudoku cages) - using grid-level style with cell boolean flags
  const renderInnerBorders = () => {
    const borders = [];

    if (cell.inner_border_top && grid?.inner_border_style) {
      borders.push(
        <div
          key="inner-border-top"
          className={styles.innerBorderTop}
          style={{
            borderTopStyle: grid.inner_border_style,
            borderTopColor: '#000',
            borderTopWidth: '2px',
          }}
        />
      );
    }

    if (cell.inner_border_right && grid?.inner_border_style) {
      borders.push(
        <div
          key="inner-border-right"
          className={styles.innerBorderRight}
          style={{
            borderRightStyle: grid.inner_border_style,
            borderRightColor: '#000',
            borderRightWidth: '2px',
          }}
        />
      );
    }

    if (cell.inner_border_bottom && grid?.inner_border_style) {
      borders.push(
        <div
          key="inner-border-bottom"
          className={styles.innerBorderBottom}
          style={{
            borderBottomStyle: grid.inner_border_style,
            borderBottomColor: '#000',
            borderBottomWidth: '2px',
          }}
        />
      );
    }

    if (cell.inner_border_left && grid?.inner_border_style) {
      borders.push(
        <div
          key="inner-border-left"
          className={styles.innerBorderLeft}
          style={{
            borderLeftStyle: grid.inner_border_style,
            borderLeftColor: '#000',
            borderLeftWidth: '2px',
          }}
        />
      );
    }

    return borders;
  };

  // Render selection borders - different logic for play vs create modes
  const renderSelectionBorders = () => {
    const borders = [];

    if (!grid?.selection_border_style) {
      return borders; // No selection style defined
    }

    if (grid.send_select) {
      // CREATE MODE: Use backend-provided selection borders
      if (cell.selection_border_top) {
        borders.push(
          <div
            key="selection-border-top"
            className={styles.selectionBorderTop}
            style={{
              borderTopStyle: grid.selection_border_style,
              borderTopColor: '#007bff',
              borderTopWidth: '3px',
            }}
          />
        );
      }

      if (cell.selection_border_right) {
        borders.push(
          <div
            key="selection-border-right"
            className={styles.selectionBorderRight}
            style={{
              borderRightStyle: grid.selection_border_style,
              borderRightColor: '#007bff',
              borderRightWidth: '3px',
            }}
          />
        );
      }

      if (cell.selection_border_bottom) {
        borders.push(
          <div
            key="selection-border-bottom"
            className={styles.selectionBorderBottom}
            style={{
              borderBottomStyle: grid.selection_border_style,
              borderBottomColor: '#007bff',
              borderBottomWidth: '3px',
            }}
          />
        );
      }

      if (cell.selection_border_left) {
        borders.push(
          <div
            key="selection-border-left"
            className={styles.selectionBorderLeft}
            style={{
              borderLeftStyle: grid.selection_border_style,
              borderLeftColor: '#007bff',
              borderLeftWidth: '3px',
            }}
          />
        );
      }
    } else {
      // PLAY MODE: Frontend handles selection locally
      if (isSelected) {
        const selectionStyle = {
          borderStyle: grid.selection_border_style,
          borderColor: '#007bff',
          borderWidth: '3px',
        };

        borders.push(
          <div
            key="selection-border-top"
            className={styles.selectionBorderTop}
            style={{
              borderTopStyle: selectionStyle.borderStyle,
              borderTopColor: selectionStyle.borderColor,
              borderTopWidth: selectionStyle.borderWidth,
            }}
          />,
          <div
            key="selection-border-right"
            className={styles.selectionBorderRight}
            style={{
              borderRightStyle: selectionStyle.borderStyle,
              borderRightColor: selectionStyle.borderColor,
              borderRightWidth: selectionStyle.borderWidth,
            }}
          />,
          <div
            key="selection-border-bottom"
            className={styles.selectionBorderBottom}
            style={{
              borderBottomStyle: selectionStyle.borderStyle,
              borderBottomColor: selectionStyle.borderColor,
              borderBottomWidth: selectionStyle.borderWidth,
            }}
          />,
          <div
            key="selection-border-left"
            className={styles.selectionBorderLeft}
            style={{
              borderLeftStyle: selectionStyle.borderStyle,
              borderLeftColor: selectionStyle.borderColor,
              borderLeftWidth: selectionStyle.borderWidth,
            }}
          />
        );
      }
    }

    return borders;
  };

  return (
    <div 
      className={`${styles.cell} ${isSelected ? styles.cellSelected : ''}`} 
      style={getCellStyle()}
      onClick={() => handleCellClick(cell.x, cell.y)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={cell.move_allowed ? 0 : -1} // Make selectable cells focusable
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCellClick(cell.x, cell.y);
        }
      }}
      aria-selected={isSelected}
      role="gridcell"
    >
      {/* Render border layers in correct order: outer → inner → selection */}
      {renderOuterBorders()}
      {renderInnerBorders()}
      {renderSelectionBorders()}
      
      {cell.header_text && (
        <div className={styles.cellHeader} style={getHeaderStyle()}>
          {cell.header_text}
        </div>
      )}
      
      <div className={styles.cellValue}>
        {cell.value_container === "circle" ? (
          <div className={styles.valueCircle}>
            {cell.value !== "0" && cell.value !== null && cell.value !== ""
              ? cell.value
              : isSelected ? <span className={styles.cellCursor}>_</span> : ""}
          </div>
        ) : (
          <>
            {cell.value !== "0" && cell.value !== null && cell.value !== ""
              ? cell.value
              : isSelected ? <span className={styles.cellCursor}>_</span> : ""}
          </>
        )}
      </div>
      
      {cell.footer_text && cell.footer_text.length > 0 && (
        <div className={styles.cellFooter} style={getFooterStyle()}>
          {renderFooterText()}
        </div>
      )}
    </div>
  );
};

export default Cell;
